import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTaxonomyCascade } from "@/hooks/useTaxonomyCascade";
import { useNewTicketValidation } from "@/components/newTicket/useNewTicketValidation";

const EMPTY_FORM = (initialType) => ({
  ticket_type: initialType || "Inbound",
  country_code: "+974",
  country_label: "Qatar",
  contact_number: "",
  contact_mode: "",
  language: "",
  customer_name: "",
  email_address: "",
  alternate_number: "",
  main_category_id: "",
  sub_category_id: "",
  issue_type_id: "",
  branch: "",
  customer_concern: "",
  attachment_ids: [],
});

/**
 * Form + side-effect orchestration for NewTicketDialog.
 * Owns: form state, lookup loading, taxonomy cascade, customer autocomplete,
 * validation, submit + writeback conflict handling, and "type switched" friction.
 */
export function useNewTicketForm({
  open, initialType, prefillCustomerId,
  onCreated, onOpenChange,
}) {
  // Lookup data
  const [countryCodes, setCountryCodes] = useState([]);
  const [contactModes, setContactModes] = useState([]);
  const [channelsList, setChannelsList] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);

  // Form
  const [form, setForm] = useState(EMPTY_FORM(initialType));

  // Taxonomy cascade (driven by form ids)
  const {
    mainCats, subCats, issueTypes, auto: autoFields,
    mainName, subName, issueName,
  } = useTaxonomyCascade({
    enabled: open,
    mainId: form.main_category_id,
    subId: form.sub_category_id,
    issueId: form.issue_type_id,
  });

  // Type-switch friction (Layer 6)
  const [typeChangedAt, setTypeChangedAt] = useState(0);
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);

  // Customer autocomplete & conflict modal
  const [customerMatches, setCustomerMatches] = useState([]);
  const [matchedCustomer, setMatchedCustomer] = useState(null);
  // Digits the user explicitly unlinked — suppresses silent auto-pick until the
  // number changes, so "Unlink" stays unlinked while the same number is typed.
  const unlinkedDigitsRef = useRef(null);
  const [conflictModal, setConflictModal] = useState(null);
  const [conflictChoices, setConflictChoices] = useState({});

  // Dynamic conditional fields (Settings-driven)
  const [dynamicFields, setDynamicFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const setCustomValue = (key, val) => setCustomValues((c) => ({ ...c, [key]: val }));

  // Load top-level lookups when dialog opens
  useEffect(() => {
    if (!open) return;
    if (initialType) setForm((f) => ({ ...f, ticket_type: initialType }));
    setTypeChangedAt(0);
    setConfirmSwitch(false);
    Promise.all([
      api.get("/country-codes"),
      api.get("/contact-modes"),
      api.get("/languages"),
      api.get("/branches"),
      api.get("/ticket-types"),
      api.get("/channels"),
    ]).then(([cc, cm, lg, br, tt, ch]) => {
      setCountryCodes(cc.data);
      setContactModes(cm.data.filter((x) => x.active));
      setLanguages(lg.data.filter((x) => x.active));
      setBranches(br.data.filter((x) => x.active));
      setTicketTypes(tt.data.filter((x) => x.active));
      setChannelsList(ch.data);
      const def = cc.data.find((c) => c.is_default);
      if (def) setForm((f) => ({ ...f, country_code: def.code, country_label: def.country }));
    });
  }, [open, initialType]);

  const pickExistingCustomer = (c) => {
    setMatchedCustomer(c);
    setCustomerMatches([]);
    setForm((f) => {
      const cc = c.country_code || f.country_code;
      const matched = countryCodes.find((x) => x.code === cc);
      return {
        ...f,
        country_code: cc,
        country_label: matched?.country || f.country_label,
        contact_number: (c.phone || f.contact_number).replace(/\D/g, ""),
        customer_name: c.name || f.customer_name,
        email_address: c.email || f.email_address,
        language: c.language || f.language,
        alternate_number: c.alt_phone || f.alternate_number,
        online_order_no: c.online_order_no || f.online_order_no,
        store_location: c.store_location || f.store_location,
      };
    });
    toast.success(`Loaded ${c.name}'s details from customer record`);
  };

  // Pre-fill customer when dialog opens with a customerId hint
  useEffect(() => {
    if (!open || !prefillCustomerId) return;
    let cancelled = false;
    api.get(`/customers/${prefillCustomerId}`).then((r) => {
      if (cancelled || !r.data) return;
      pickExistingCustomer(r.data);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillCustomerId]);

  // Debounced customer autocomplete on phone digits
  useEffect(() => {
    if (!open) return;
    const digits = (form.contact_number || "").replace(/\D/g, "");
    // Once the number changes away from the unlinked one, re-enable auto-pick.
    if (unlinkedDigitsRef.current && unlinkedDigitsRef.current !== digits) {
      unlinkedDigitsRef.current = null;
    }
    if (digits.length < 4) {
      setCustomerMatches([]);
      return;
    }
    if (matchedCustomer) {
      const matchedDigits = (matchedCustomer.phone || "").replace(/\D/g, "");
      if (matchedDigits.startsWith(digits) || digits.startsWith(matchedDigits)) return;
      setMatchedCustomer(null);
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/search",
          { params: { q: digits, by: "phone", limit: 6 } });
        if (cancelled) return;
        const results = data || [];
        // Auto-prefill: when the full number is entered AND exactly one customer
        // matches, link + populate silently (reversible via Unlink). When several
        // match — or the user just unlinked this number — fall back to the
        // click-to-pick dropdown.
        const expected = countryCodes.find((c) => c.code === form.country_code)?.phone_digits;
        const fullNumberEntered = expected ? digits.length >= expected : digits.length >= 8;
        const suppressed = unlinkedDigitsRef.current === digits;
        if (results.length === 1 && fullNumberEntered && !suppressed) {
          setCustomerMatches([]);
          pickExistingCustomer(results[0]);
        } else {
          setCustomerMatches(results);
        }
      } catch {
        if (!cancelled) setCustomerMatches([]);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contact_number, form.country_code, open, matchedCustomer]);

  const reset = () => { setForm(EMPTY_FORM()); setCustomValues({}); setDynamicFields([]); val.reset(); };

  // Resolve dynamic conditional fields whenever the ticket context changes.
  useEffect(() => {
    if (!open) { setDynamicFields([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/dynamic-fields/resolve", {
          params: {
            main_category_id: form.main_category_id || undefined,
            sub_category_id: form.sub_category_id || undefined,
            issue_type_id: form.issue_type_id || undefined,
            department_id: autoFields.department_id || undefined,
            channel: form.contact_mode || undefined,
            ticket_type: form.ticket_type || undefined,
          },
        });
        if (cancelled) return;
        setDynamicFields(data || []);
        // prune values for fields no longer visible
        const keys = new Set((data || []).map((d) => d.key));
        setCustomValues((cv) => {
          const next = {};
          Object.keys(cv).forEach((k) => { if (keys.has(k)) next[k] = cv[k]; });
          return next;
        });
      } catch {
        if (!cancelled) setDynamicFields([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, form.main_category_id, form.sub_category_id, form.issue_type_id,
      autoFields.department_id, form.contact_mode, form.ticket_type]);

  // Validation
  const errors = useMemo(() => {
    const e = {};
    const ccDigits = (form.contact_number || "").replace(/\D/g, "");
    const reqLen = countryCodes.find((c) => c.code === form.country_code)?.phone_digits;
    if (!ccDigits) {
      e.contact_number = "Required";
    } else if (reqLen && ccDigits.length !== reqLen) {
      e.contact_number = `Must be exactly ${reqLen} digits`;
    } else if (!reqLen && ccDigits.length < 6) {
      e.contact_number = "Min 6 digits";
    }
    if (!form.contact_mode) e.contact_mode = "Required";
    if (!form.language) e.language = "Required";
    if (!form.customer_name) e.customer_name = "Required";
    if (!form.main_category_id) e.main_category_id = "Required";
    if (!form.sub_category_id) e.sub_category_id = "Required";
    if (!form.issue_type_id) e.issue_type_id = "Required";
    if (!form.customer_concern || form.customer_concern.length < 20) {
      e.customer_concern = "Min 20 characters";
    }
    if (autoFields.branch_required && !form.branch) {
      e.branch = "Branch required for this issue type";
    }
    // Dynamic conditional fields — required + visible
    dynamicFields.forEach((d) => {
      if (!d.required) return;
      const v = customValues[d.key];
      const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) e[`cf_${d.key}`] = `${d.label} is required`;
    });
    return e;
  }, [form, autoFields, dynamicFields, customValues, countryCodes]);

  // Validation surfacing (touched/submit gating, missing-fields list, scroll).
  const val = useNewTicketValidation({ errors, dynamicFields, open });

  // Fill ratio for switch-friction warning (Layer 4)
  const fillRatio = useMemo(() => {
    const tracked = [
      form.contact_number, form.contact_mode, form.language, form.customer_name,
      form.main_category_id, form.sub_category_id, form.issue_type_id,
      form.customer_concern && form.customer_concern.length >= 20 ? "1" : "",
    ];
    const filled = tracked.filter(Boolean).length;
    return filled / tracked.length;
  }, [form]);
  const switchWarn = !!typeChangedAt && fillRatio >= 0.4;

  const setTicketType = (val) => {
    if (val === form.ticket_type) return;
    setForm({ ...form, ticket_type: val });
    setTypeChangedAt(Date.now());
    setConfirmSwitch(false);
  };

  const submit = async () => {
    if (val.gateInvalid()) return;
    const switchedRecently = typeChangedAt && (Date.now() - typeChangedAt < 10_000);
    if (switchedRecently && !confirmSwitch) {
      setConfirmSwitch(true);
      toast.message(`You just switched type → confirm to create as ${form.ticket_type}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        category: mainName,
        subcategory: subName,
        priority: autoFields.priority || "medium",
        status: "in_progress",
        channel: form.contact_mode,
        type: autoFields.case_type_name || "Complaint",
        subject: issueName || mainName,
        description: form.customer_concern,
        attachment_ids: form.attachment_ids,
        country_code: form.country_code,
        contact_number: form.contact_number,
        contact_mode: form.contact_mode,
        language: form.language,
        email_address: form.email_address,
        alternate_number: form.alternate_number,
        main_category_id: form.main_category_id,
        sub_category_id: form.sub_category_id,
        issue_type_id: form.issue_type_id,
        issue_type_name: issueName,
        department_id: autoFields.department_id,
        department_name: autoFields.department_name,
        case_type_id: autoFields.case_type_id,
        case_type_name: autoFields.case_type_name,
        branch: form.branch,
        sla_minutes: autoFields.sla_minutes || 480,
        customer_concern: form.customer_concern,
        custom_fields: customValues,
      };
      if (conflictModal && Object.keys(conflictChoices).length) {
        const confirmed = {};
        conflictModal.conflicts.forEach((c) => {
          if (conflictChoices[c.field] === "update") confirmed[c.field] = c.to;
        });
        payload.customer_writeback_confirmed = confirmed;
      }
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket ${data.code} created · ${autoFields.department_name || "team"} notified`);
      reset();
      setConflictModal(null);
      setConflictChoices({});
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "customer_writeback_conflict") {
        const choices = {};
        detail.conflicts.forEach((c) => { choices[c.field] = "keep"; });
        setConflictChoices(choices);
        setConflictModal(detail);
        setSubmitting(false);
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.error || "Failed to create ticket"));
    } finally {
      setSubmitting(false);
    }
  };

  const closeConflictModal = () => { setConflictModal(null); setConflictChoices({}); };

  return {
    // lookups
    countryCodes, contactModes, channelsList, languages, branches, ticketTypes,
    // form
    form, setForm, reset,
    // cascade
    mainCats, subCats, issueTypes, autoFields, mainName, subName, issueName,
    // dynamic conditional fields
    dynamicFields, customValues, setCustomValue,
    // type switching
    setTicketType, switchWarn, confirmSwitch,
    // customer
    customerMatches, matchedCustomer,
    pickExistingCustomer,
    unlinkCustomer: () => {
      unlinkedDigitsRef.current = (form.contact_number || "").replace(/\D/g, "");
      setMatchedCustomer(null);
    },
    // conflict modal
    conflictModal, conflictChoices, setConflictChoices, closeConflictModal,
    // submit
    submit, submitting, errors,
    // validation surfacing
    validation: val.validation,
    missingFields: val.missingFields, submitAttempted: val.submitAttempted,
  };
}
